import { normalizeEmailDashes } from "./emailImportNormalize";

/**
 * Parse text copied from website visitor-form notification emails (Dutch).
 * Supports multiple submissions in one paste (each starting with the standard header line).
 */

export type ParsedBezoekerForm = {
  first_name: string;
  last_name: string;
  birth_date?: string;
  email?: string;
  phone?: string;
  address?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  notes?: string;
  wants_newsletter: boolean;
  wants_activities_info: boolean;
  willing_volunteer: boolean;
};

export type ParseBlockResult =
  | { ok: true; data: ParsedBezoekerForm }
  | { ok: false; error: string };

/** Split pasted email into one block per form submission. */
export function splitBezoekerFormBlocks(raw: string): string[] {
  const t = raw.replace(/\r\n/g, "\n").trim();
  if (!t) return [];

  const hasHeader = /^Er is een nieuw bezoekersformulier ingevuld\s*:?\s*$/gim.test(t);
  if (!hasHeader) return [t];

  const parts = t.split(/(?=^Er is een nieuw bezoekersformulier ingevuld\s*:?\s*$)/gim);
  return parts.map((p) => p.trim()).filter(Boolean);
}

function lineValue(lines: string[], re: RegExp): string | undefined {
  for (const line of lines) {
    const m = line.match(re);
    if (m?.[1] != null) return m[1].trim();
  }
  return undefined;
}

/** True if the user agreed (Ingestemd), false for Niet ingestemd or unknown. */
export function parseConsentPhrase(rest: string): boolean {
  const t = rest.trim();
  if (!t) return false;
  if (/\bNiet\s+ingestemd\b/i.test(t)) return false;
  if (/\bIngestemd\b/i.test(t)) return true;
  return false;
}

function normalizeBirthDate(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const s = raw.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (m) {
    const d = m[1].padStart(2, "0");
    const mo = m[2].padStart(2, "0");
    return `${m[3]}-${mo}-${d}`;
  }
  return s;
}

function normalizeEmail(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  let s = raw.replace(/\u00a0/g, " ").trim();
  if (!s) return undefined;
  const angle = s.match(/<([^<>@\s]+@[^<>]+)>/);
  if (angle) s = angle[1].trim();
  const token = s.match(/[^\s<>,;]+@[^\s<>,;]+\.[^\s<>,;]+/);
  if (token) return normalizeEmailDashes(token[0].toLowerCase());
  return normalizeEmailDashes(s.toLowerCase());
}

function normalizePhone(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  return raw.replace(/\s+/g, " ").trim() || undefined;
}

function normalizePostcode(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  return raw.replace(/\s+/g, "").trim().toUpperCase() || undefined;
}

/**
 * Parse one block of text (one form). Requires Naam + Achternaam.
 */
export function parseBezoekerFormBlock(block: string): ParseBlockResult {
  const lines = block.split(/\n/).map((l) => l.replace(/\r$/, ""));

  const first_name =
    lineValue(lines, /^\s*Naam\s*:\s*(.+)$/i) ??
    lineValue(lines, /^\s*Voornaam\s*:\s*(.+)$/i);
  const last_name = lineValue(lines, /^\s*Achternaam\s*:\s*(.+)$/i);

  if (!first_name || !last_name) {
    return {
      ok: false,
      error:
        "Verwacht regels ‘Naam:’ (of ‘Voornaam:’) en ‘Achternaam:’. Plak de volledige e-mailtekst of controleer of er niets ontbreekt.",
    };
  }

  const birth_raw = lineValue(lines, /^\s*Geboortedatum\s*:\s*(.+)$/i);
  const address = lineValue(lines, /^\s*Straat\s*:\s*(.+)$/i);
  const postal_code = normalizePostcode(lineValue(lines, /^\s*Postcode\s*:\s*(.+)$/i));
  const city = lineValue(lines, /^\s*Woonplaats\s*:\s*(.+)$/i);
  const phone = normalizePhone(lineValue(lines, /^\s*Telefoon(?:nummer)?\s*:\s*(.+)$/i));
  const email = normalizeEmail(
    lineValue(lines, /^\s*E-?mail(?:\s*-?\s*adres)?\s*:\s*(.+)$/i)
  );

  const wants_newsletter = parseConsentPhrase(
    lineValue(lines, /^\s*Wil\s+nieuwsbrief\s*:\s*(.+)$/i) ?? ""
  );
  const wants_activities_info = parseConsentPhrase(
    lineValue(lines, /^\s*Wil\s+informatie\s+over\s+activiteiten\s*:\s*(.+)$/i) ?? ""
  );
  const willing_volunteer = parseConsentPhrase(
    lineValue(
      lines,
      /^\s*Bereid[^:\n]*vrijwilligerswerk\s*:\s*(.+)$/i
    ) ?? ""
  );

  const data: ParsedBezoekerForm = {
    first_name,
    last_name,
    birth_date: normalizeBirthDate(birth_raw),
    email,
    phone,
    address,
    postal_code,
    city,
    country: undefined,
    notes: undefined,
    wants_newsletter,
    wants_activities_info,
    willing_volunteer,
  };

  return { ok: true, data };
}

/** Parse full paste: multiple form blocks if the header line repeats. */
export function parseBezoekerFormEmail(raw: string): ParseBlockResult[] {
  const blocks = splitBezoekerFormBlocks(raw);
  return blocks.map((block) => parseBezoekerFormBlock(block));
}
